/**
 * Citation numbers follow their order in the text. The FE sends a `{citationId -> number}` map to
 * `/chat/draft/get-version`, and the BE puts the numbers into the markdown. Drafts use the chat's
 * order, so numbers aren't contiguous; only citations within one cluster are sorted.
 */

// Markdown links with an integer label: `[12](…)`. The destination can't contain spaces or `)`.
const NUMBERED_LINK_REGEX = /\[(\d+)\]\(([^)\s]*)\)/g;

/** Drafts also contain ordinary links (and `[1]`-style plain text); only the citation ones carry a documentId. */
const isCitationHref = (href: string): boolean => /[?&]documentId=/.test(href);

/**
 * A run of citation links with nothing but whitespace between them — the group
 * the model emits at the end of a sentence.
 */
const CITATION_CLUSTER_REGEX = /\[\d+\]\([^)\s]*\)(?:\s*\[\d+\]\([^)\s]*\))+/g;

/**
 * Sorts each cluster of adjacent citations ascending, so a group reads `[3][4][8]` rather
 * than whatever order the model emitted.
 */
export const sortDraftCitationClusters = (content: string): string =>
  content.replace(new RegExp(CITATION_CLUSTER_REGEX), (cluster) => {
    const links: { number: number; text: string }[] = [];
    // Whitespace between the links, reused as-is so spacing never changes.
    const separators: string[] = [];
    const linkRegex = new RegExp(NUMBERED_LINK_REGEX);
    let endOfPreviousLink = 0;
    let match = linkRegex.exec(cluster);

    while (match) {
      // A non-citation link in the run means this isn't a citation cluster.
      if (!isCitationHref(match[2])) return cluster;
      if (links.length > 0) separators.push(cluster.slice(endOfPreviousLink, match.index));
      links.push({ number: Number(match[1]), text: match[0] });
      endOfPreviousLink = match.index + match[0].length;
      match = linkRegex.exec(cluster);
    }

    if (links.length < 2) return cluster;

    return [...links]
      .sort((a, b) => a.number - b.number)
      .map((link, index) => (index > 0 ? separators[index - 1] : '') + link.text)
      .join('');
  });

/** Builds the 1-based `{citationId -> number}` map the BE expects from an ordered id list. */
export const citationIndexMapFromOrder = (orderedCitationIds: string[]): Record<string, number> => {
  const map: Record<string, number> = {};
  orderedCitationIds.forEach((id, index) => {
    map[id] = index + 1;
  });
  return map;
};
